import { useState, useEffect } from 'react';
import { useJwt, useAuthHeaders, EventBus } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Modal, Button, Badge, Icon } from '../ds/index.js';

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  meetLink: string | null;
  calendarLink: string | null;
}

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

/** Date sans heure (« 2026-10-05 ») = rendez-vous sur la journée entière. */
const isAllDay = (iso: string) => iso.length === 10;
/** Une date seule s'interprète en heure locale (new Date('2026-10-05') serait en UTC). */
const toDate = (iso: string) => (isAllDay(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso));
const hm = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

function dayLabel(d: Date): string {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const date = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  if (d.toDateString() === today.toDateString()) return `Aujourd'hui · ${date}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Demain · ${date}`;
  return date;
}

function timeLabel(ev: CalendarEvent): string {
  if (isAllDay(ev.start)) return 'Toute la journée';
  const s = new Date(ev.start);
  const e = new Date(ev.end);
  if (s.toDateString() === e.toDateString()) return `${hm(s)} – ${hm(e)}`;
  return `${hm(s)} – ${e.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${hm(e)}`;
}

/** En cours ou qui commence dans moins de 15 minutes : bouton Rejoindre mis en avant. */
function isSoon(ev: CalendarEvent): boolean {
  if (isAllDay(ev.start)) return false;
  const now = Date.now();
  return new Date(ev.start).getTime() - now < 15 * 60_000 && new Date(ev.end).getTime() > now;
}

/**
 * Google Agenda configuré sur cette instance ? `false` tant que la réponse
 * n'est pas arrivée ou en cas d'erreur : l'entrée Agenda reste masquée.
 */
export function useGoogleCalendarAvailable(): boolean {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!jwt || !tenantId) return;
    let cancelled = false;
    fetch(`${API_BASE}/integration/google/available`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() as Promise<{ available?: boolean }> : { available: false }))
      .then((data) => { if (!cancelled) setAvailable(data.available === true); })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, [jwt, tenantId]);

  return available;
}

/**
 * CalendarModal — « Agenda » : les rendez-vous Google Calendar des 7 prochains
 * jours, regroupés par jour, avec un bouton Rejoindre quand il y a une visio.
 */
export function CalendarModal({ onClose }: { onClose: () => void }) {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);

  const [connected, setConnected] = useState<boolean | null>(null); // null = chargement
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try { EventBus.emit('ui:modal-opened', {}); } catch {}
    return () => { try { EventBus.emit('ui:modal-closed', {}); } catch {} };
  }, []);

  useEffect(() => {
    if (!jwt || !tenantId) return;
    let cancelled = false;

    fetch(`${API_BASE}/integration/google/events`, { headers: authHeaders })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setConnected(false);
          return;
        }
        if (res.status === 401) {
          setConnected(false);
          setError('La connexion à Google Calendar a expiré. Reconnecte ton agenda.');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as { events: CalendarEvent[] };
        setConnected(true);
        setEvents(data.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger tes rendez-vous. Réessaie dans un instant.');
      });

    return () => { cancelled = true; };
  }, [jwt, tenantId]);

  async function handleConnect() {
    if (!jwt) return;
    try {
      const res = await fetch(`${API_BASE}/integration/google/auth`, { headers: authHeaders });
      if (res.status === 503) {
        setError('La connexion à Google Agenda n\u2019est pas encore disponible sur cet espace.');
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json() as { url: string };
      window.location.href = data.url;
    } catch {
      setError('Impossible de lancer la connexion à Google.');
    }
  }

  async function handleDisconnect() {
    if (!jwt) return;
    try {
      await fetch(`${API_BASE}/integration/google/disconnect`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      setConnected(false);
      setEvents([]);
    } catch {
      setError("La déconnexion n'a pas abouti. Réessaie.");
    }
  }

  // Regroupement par jour (les événements arrivent triés par l'API)
  const days: Array<{ label: string; events: CalendarEvent[] }> = [];
  for (const ev of events) {
    const label = dayLabel(toDate(ev.start));
    const last = days[days.length - 1];
    if (last && last.label === label) last.events.push(ev);
    else days.push({ label, events: [ev] });
  }

  return (
    <Modal
      title="Agenda"
      size="lg"
      onClose={onClose}
      footer={connected ? <Button variant="ghost" size="sm" onClick={() => { void handleDisconnect(); }}>Déconnecter Google Calendar</Button> : undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: 'var(--color-text-primary)' }}>
        {error && <div className="pl-alert" role="alert">{error}</div>}

        {connected === null && !error && <p className="pl-lead">Chargement…</p>}

        {connected === false && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '32px 24px', textAlign: 'center' }}>
            <div aria-hidden="true" style={{ width: 64, height: 64, borderRadius: 'var(--radius-full)', background: 'var(--color-primary-muted)', color: 'var(--color-primary-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="calendar" size={28} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380 }}>
              <h3 style={{ margin: 0, font: '700 18px/1.25 var(--font-display)' }}>Connecte ton agenda</h3>
              <p className="pl-lead">
                Tes rendez-vous des 7 prochains jours s'affichent ici, avec un bouton pour rejoindre la visio en un clic. Placio lit ton agenda, il ne le modifie jamais.
              </p>
            </div>
            <Button variant="primary" onClick={() => { void handleConnect(); }}>Connecter Google Calendar</Button>
          </div>
        )}

        {connected === true && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <p className="pl-lead">
                Tes 7 prochains jours · {events.length === 0 ? 'aucun rendez-vous' : `${events.length} rendez-vous`}
              </p>
              <Badge tone="success">Google Calendar connecté</Badge>
            </div>

            {events.length === 0 && (
              <p className="pl-lead" style={{ color: 'var(--color-text-muted)', padding: '16px 0', textAlign: 'center' }}>
                Rien de prévu pour l'instant.
              </p>
            )}

            {days.map((day) => (
              <section key={day.label} aria-label={day.label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h3 className="pl-day">{day.label}</h3>
                {day.events.map((ev) => (
                  <article key={ev.id} className="pl-ev">
                    <span className="pl-ev-time">{timeLabel(ev)}</span>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {ev.calendarLink ? (
                        <a href={ev.calendarLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 15, fontWeight: 500, color: 'inherit', textDecoration: 'none' }}>{ev.title}</a>
                      ) : (
                        <span style={{ fontSize: 15, fontWeight: 500 }}>{ev.title}</span>
                      )}
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{ev.meetLink ? 'Google Meet' : 'Sans lien de visio'}</span>
                    </div>
                    {ev.meetLink && (
                      <Button
                        variant={isSoon(ev) ? 'primary' : 'secondary'}
                        size="sm"
                        icon="video"
                        onClick={() => window.open(ev.meetLink!, '_blank', 'noopener,noreferrer')}
                      >
                        Rejoindre
                      </Button>
                    )}
                  </article>
                ))}
              </section>
            ))}
          </>
        )}
      </div>
    </Modal>
  );
}
